import {
  BlockContainer,
  FindDealerList,
  FindDealerProvider,
  FindDealerSearch,
  LoadingError,
  LocationButton,
  Title,
} from '@components';
import { Grid, Stack, useMediaQuery, useTheme } from '@mui/material';

import { useGetDealerListQuery } from '@api/customer.api';
import { TextCodes } from '@api/types/translation/TextCodes';
import { FindDealerFilter, FindDealerMap, useFindDealerContext } from '@components';
import { LoadScriptProps, useJsApiLoader } from '@react-google-maps/api';
import { getSystemPages } from '@store/app/app.slice';
import { t } from '@translations';
import { useSelector } from 'react-redux';

type FindDealerBlockProps = {
  title: string;
};
const libraries: LoadScriptProps['libraries'] = ['places'];

export const FindDealerBlock = ({ title }: FindDealerBlockProps) => {
  const systemPages = useSelector(getSystemPages);
  const googleMapApiKey = systemPages.googleMapApiKey as string;
  const { isLoaded, loadError } = useJsApiLoader({
    region: 'no',
    language: 'no',
    googleMapsApiKey: googleMapApiKey,
    libraries,
  });

  const { currentData, isError, isLoading, isFetching } = useGetDealerListQuery({
    offSet: 0,
    limit: 1000,
    filters: [
      {
        filterName: 'SearchByName',
      },
    ],
  });
  const isWorking = isLoading || isFetching;
  const theme = useTheme();

  const isBreakpointLarge = useMediaQuery(theme.breakpoints.up('lg'));
  if (isError || loadError) {
    return <LoadingError />;
  }
  if (!isLoaded) {
    return null;
  }

  return (
    <FindDealerProvider fetchedDealers={currentData?.data?.customersListFound}>
      <BlockContainer>
        <Title sx={{ mb: { xs: 1, lg: 4 } }}>{title || t(TextCodes.Dealer.Find.FindDealer)}</Title>
        {!isBreakpointLarge && (
          <Stack
            direction={{ base: 'column', sm: 'row' }}
            spacing={2}
            sx={{ flexDirection: 'column', mb: 2 }}
          >
            <FindDealerSearch />
            <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
              <FindDealerFilter />
              <FindUserLocationButton />
            </Stack>
          </Stack>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <FindDealerMap isWorking={isWorking} />
          </Grid>
          <Grid item xs={12} lg={6}>
            {isBreakpointLarge && (
              <Stack direction={{ base: 'column', sm: 'row' }} spacing={2}>
                <FindDealerSearch />
                <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                  <FindDealerFilter />
                  <FindUserLocationButton />
                </Stack>
              </Stack>
            )}
            <FindDealerList />
          </Grid>
        </Grid>
      </BlockContainer>
    </FindDealerProvider>
  );
};

const FindUserLocationButton = () => {
  const { requestLocation } = useFindDealerContext();
  return <LocationButton variant='tertiary' onClickEvent={requestLocation} />;
};
